import { useCallback, useState } from 'react';

export type GridColumnWidths<TColumnKey extends string> = Record<TColumnKey, number>;

interface GridSettings<TColumnKey extends string, TSort, TFilters> {
    columnWidths: GridColumnWidths<TColumnKey>;
    filters: TFilters;
    sort: TSort;
}

interface PersistedGridSettings<TColumnKey extends string> {
    columnWidths?: Partial<Record<TColumnKey, unknown>>;
    filters?: unknown;
    sort?: unknown;
    version?: unknown;
}

interface UseGridLocalStorageSettingsOptions<TColumnKey extends string, TSort, TFilters> {
    defaultColumnWidths: GridColumnWidths<TColumnKey>;
    defaultFilters: TFilters;
    defaultSort: TSort;
    storageKey: string;
    validateFilters?: (value: unknown) => value is TFilters;
    validateSort?: (value: unknown) => value is TSort;
    version?: number;
}

const canUseLocalStorage = () => {
    try {
        return typeof window !== 'undefined' && !!window.localStorage;
    } catch {
        return false;
    }
};

const isRecord = (value: unknown): value is Record<string, unknown> => {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
};

const readPersistedSettings = <TColumnKey extends string>(
    storageKey: string,
    version: number
): PersistedGridSettings<TColumnKey> | null => {
    if (!canUseLocalStorage()) {
        return null;
    }

    try {
        const rawValue = window.localStorage.getItem(storageKey);

        if (!rawValue) {
            return null;
        }

        const parsedValue = JSON.parse(rawValue);

        if (!isRecord(parsedValue) || parsedValue.version !== version) {
            return null;
        }

        return parsedValue as PersistedGridSettings<TColumnKey>;
    } catch {
        return null;
    }
};

const normalizeColumnWidths = <TColumnKey extends string>(
    defaultColumnWidths: GridColumnWidths<TColumnKey>,
    persistedColumnWidths: PersistedGridSettings<TColumnKey>['columnWidths']
) => {
    const columnWidths = { ...defaultColumnWidths };

    if (!isRecord(persistedColumnWidths)) {
        return columnWidths;
    }

    for (const key of Object.keys(defaultColumnWidths) as TColumnKey[]) {
        const value = persistedColumnWidths[key];

        if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
            columnWidths[key] = value;
        }
    }

    return columnWidths;
};

const buildInitialSettings = <TColumnKey extends string, TSort, TFilters>({
    defaultColumnWidths,
    defaultFilters,
    defaultSort,
    storageKey,
    validateFilters,
    validateSort,
    version = 1
}: UseGridLocalStorageSettingsOptions<TColumnKey, TSort, TFilters>): GridSettings<TColumnKey, TSort, TFilters> => {
    const persistedSettings = readPersistedSettings<TColumnKey>(storageKey, version);

    if (!persistedSettings) {
        return {
            columnWidths: { ...defaultColumnWidths },
            filters: defaultFilters,
            sort: defaultSort
        };
    }

    return {
        columnWidths: normalizeColumnWidths(defaultColumnWidths, persistedSettings.columnWidths),
        filters: validateFilters?.(persistedSettings.filters) ? persistedSettings.filters : defaultFilters,
        sort: validateSort?.(persistedSettings.sort) ? persistedSettings.sort : defaultSort
    };
};

export const useGridLocalStorageSettings = <TColumnKey extends string, TSort, TFilters>(
    options: UseGridLocalStorageSettingsOptions<TColumnKey, TSort, TFilters>
) => {
    const { storageKey, version = 1 } = options;
    const [settings, setSettingsState] = useState(() => buildInitialSettings(options));

    const persistSettings = useCallback((nextSettings: GridSettings<TColumnKey, TSort, TFilters>) => {
        if (!canUseLocalStorage()) {
            return;
        }

        try {
            window.localStorage.setItem(storageKey, JSON.stringify({
                ...nextSettings,
                version
            }));
        } catch {
            // localStorage can be unavailable in private mode or blocked by policy.
        }
    }, [storageKey, version]);

    const setSettings = useCallback((
        updater: GridSettings<TColumnKey, TSort, TFilters>
            | ((currentSettings: GridSettings<TColumnKey, TSort, TFilters>) => GridSettings<TColumnKey, TSort, TFilters>)
    ) => {
        setSettingsState(currentSettings => {
            const nextSettings = typeof updater === 'function'
                ? updater(currentSettings)
                : updater;

            persistSettings(nextSettings);
            return nextSettings;
        });
    }, [persistSettings]);

    const setColumnWidths = useCallback((columnWidths: GridColumnWidths<TColumnKey>) => {
        setSettings(currentSettings => ({
            ...currentSettings,
            columnWidths
        }));
    }, [setSettings]);

    const setFilters = useCallback((filters: TFilters) => {
        setSettings(currentSettings => ({
            ...currentSettings,
            filters
        }));
    }, [setSettings]);

    const setSort = useCallback((sort: TSort) => {
        setSettings(currentSettings => ({
            ...currentSettings,
            sort
        }));
    }, [setSettings]);

    return {
        columnWidths: settings.columnWidths,
        filters: settings.filters,
        setColumnWidths,
        setFilters,
        setSort,
        sort: settings.sort
    };
};
